import * as React from 'react';
import { Button, TockDialog, Typography } from 'omakase';
import { showError, showInfo } from '@tock/toast';
import { useDispatch } from 'react-redux';

interface MobileShareLinkProps {
  /**
   * text for native share
   */
  text: string;
  /**
   * title for native share
   */
  title: string;
  /**
   * url for native share
   */
  url: string;
}

/**
 * Detects if native share is available, displays on sm and md breakpoints, but is hidden on > md
 * Expects children to be 1 element only, and will be the element the share click handler will be attached to, such as a Button, IconButton, etc
 * if native share is not available a fallback dialog will be displayed.
 * https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share
 *
 */
export const MobileShareLink: React.FunctionComponent<MobileShareLinkProps> = ({
  children,
  text,
  title,
  url,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dispatch = useDispatch();
  const canShare = typeof window !== 'undefined' && !!navigator.canShare;

  const handleClickCopy = () => {
    navigator.clipboard.writeText(url).then(
      () => {
        dispatch(showInfo('Copied to clipboard'));
      },
      () => {
        dispatch(showError('Failed to copy to clipboard. Please try again.'));
      }
    );
  };

  const handleClose = () => setIsOpen(false);

  const handleClick = async (childOnClick: () => void) => {
    if (
      canShare &&
      navigator.canShare({
        title,
        text,
        url,
      })
    ) {
      if (childOnClick) {
        childOnClick();
      }
      try {
        await navigator.share({
          title,
          text,
          url,
        });
        // eslint-disable-next-line no-empty
      } catch (_error) {}
    } else {
      if (childOnClick) {
        childOnClick();
      }
      // fallback to dialog
      setIsOpen(true);
    }
  };

  // calling this to throw error if more than one
  const childrenToMap = React.Children.only(children);

  return (
    <>
      {React.Children.map<React.ReactNode, React.ReactNode>(
        childrenToMap,
        (child: React.ReactNode) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child as React.ReactElement, {
              onClick: () => {
                handleClick(child.props.onClick);
              },
            });
          }
          return null;
        }
      )}
      <TockDialog
        headingConfig={{
          left: {
            configType: 'title',
            title,
          },
          right: {
            configType: 'closeButton',
          },
        }}
        type="sheet"
        open={isOpen}
        onClose={handleClose}
      >
        <Typography variant="body02" mb={2}>
          {text}
        </Typography>

        <Button variant="basic" size="medium" onClick={handleClickCopy}>
          Copy link
        </Button>
      </TockDialog>
    </>
  );
};
